/**
 * @see https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus#response_and_session_objects
 */
interface StatusResponse {
  status?:
    | 'connected'
    | 'not_authorized'
    | 'authorization_expired'
    | 'unknown'
    | null
    // NOTE: status is unused and untested, so leaving the unknown here so we don't trust the above data
    | unknown;
  authResponse?: {
    accessToken: string;
    expiresIn: number;
    signedRequest: string;
    userID: string;
    grantedScopes?: string;
    reauthorize_required_in?: number;
  };
}

type StatusResponseUnknown = StatusResponse | unknown;

export const isValidStatusResponse = (
  res: StatusResponseUnknown
): res is StatusResponse =>
  typeof (res as StatusResponse) === 'object' &&
  // auth response (optional)
  (typeof (res as StatusResponse).authResponse === 'undefined' ||
    (typeof (res as StatusResponse).authResponse === 'object' &&
      typeof (res as StatusResponse).authResponse?.accessToken === 'string'));

/**
 * @see https://developers.facebook.com/docs/reference/javascript/FB.login#options
 */
interface LoginOptions {
  auth_type?: 'reauthenticate' | 'reauthorize' | 'rerequest';
  /**
   * NOTE: every permission (scope) below requires App Review except for email and public_profile.
   * @see https://developers.facebook.com/docs/permissions/reference
   */
  scope?:
    | 'email'
    | 'public_profile'
    | 'user_gender'
    | 'user_likes'
    | 'user_location'
    | 'user_age_range'
    | 'user_birthday'
    | 'user_friends'
    | 'user_hometown'
    | 'user_link'
    | 'user_location'
    | 'user_photos'
    | 'user_videos'
    | 'user_posts'
    | 'user_videos';
  return_scopes?: boolean;
  enable_profile_selector?: boolean;
  profile_selector_ids?: string;
}

/**
 * @see https://developers.facebook.com/docs/javascript/reference/FB.init#parameters
 */
interface InitParams {
  appId: string;
  version: string;
  cookie?: boolean;
  status?: boolean;
  xfbml?: boolean;
  frictionlessRequests?: boolean;
  hideFlashCallback?: boolean;
  autoLogAppEvents?: boolean;
}

interface Facebook {
  /**
   * @see https://developers.facebook.com/docs/javascript/reference/FB.init
   */
  init: (params: InitParams) => void;
  /**
   * @see https://developers.facebook.com/docs/reference/javascript/FB.login
   */
  login: (
    callback: (response: StatusResponseUnknown) => void,
    options?: LoginOptions
  ) => void;
  /**
   * @see https://developers.facebook.com/docs/reference/javascript/FB.logout
   */
  logout: (callback?: (response: StatusResponseUnknown) => void) => void;
  /**
   * @see https://developers.facebook.com/docs/reference/javascript/FB.getLoginStatus
   */
  getLoginStatus: (
    callback: (data: StatusResponseUnknown) => void,
    options?: LoginOptions
  ) => void;
}

export type WindowFacebook = unknown | Facebook;

export const isFacebookValid = (fb: WindowFacebook): fb is Facebook =>
  typeof (fb as Facebook) === 'object' &&
  typeof (fb as Facebook).getLoginStatus === 'function' &&
  typeof (fb as Facebook).login === 'function' &&
  typeof (fb as Facebook).logout === 'function' &&
  typeof (fb as Facebook).init === 'function';
